import { useState, useRef, useEffect } from 'react';
import './QualitativeCollapsed.scss';
import { useSelector } from 'react-redux';
import DropdownSelect from '../../DropdownSelect/DropdownSelect';
import { t } from '../../../i18n/i18n';
import {
  getCandidateQualitativeIPVPdfExport,
  getCandidateQualitativePdfExport,
} from '../../../API/services/alkymersService';
import HorizontalLoader from '../../HorizontalLoader/HorizontalLoader';
import CloudDownload from '../../../assets/dashboard/CloudDownload';
import { downloadBase64Pdf, downloadFile } from '../../../utils/downloadFile';
import { PromptSubTypes, QualitativeTypes } from '../../../constants/enums';
import { isSFIATestGroup, someSFIATestGroup } from '../../../utils/testgroups';
import closeIcon from '../../../assets/createSearch/close-icon.svg';
import { Modal } from 'antd';

const QualitativeCollapsed = ({
  testgroups,
  recruitSessionId,
  profile,
  screeningInstance,
  setScreeningInstance,
}) => {
  const { user } = useSelector((state) => state.auth);
  const [testgroupsToQualitative, setTestgroupsToQualitative] = useState([]);
  const [selectedTestgroup, setSelectedTestgroup] = useState(testgroups[0]);
  const [candidateRole, setCandidateRole] = useState(screeningInstance?.candidateRole ?? '');
  const [modalCandidateRole, setModalCandidateRole] = useState(false);
  const [barElements, setBarElements] = useState({});
  const [roleError, setRoleError] = useState('');
  const barRefs = useRef({});
  const [reportLoading, setReportLoading] = useState(false);

  const downloadOptions = [
    {
      id: true,
      name: t('DOWNLOAD_REPORT_WITH_AI_AFFINITY'),
    },
    {
      id: false,
      name: t('DOWNLOAD_REPORT_WITHOUT_AI_AFFINITY'),
    },
  ];

  // Constants and validations
  const isIPVTestgroup = () =>
    selectedTestgroup.completeData?.qualitativeConfigResults?.type === QualitativeTypes.IPV;

  const isSFIATestgroup = selectedTestgroup.id === PromptSubTypes.SFIA_CORRECTION;

  const sfiaTestgroups = testgroups.filter((tg) => isSFIATestGroup(tg));

  const finishedSFIAScreening =
    isSFIATestgroup &&
    sfiaTestgroups?.every(
      (tg) => tg.results && typeof tg.results.score === 'number' && tg.results.explanation
    );

  const finishedQualitative = Boolean(
    selectedTestgroup?.testGroupQualitativeResults?.axisResults || finishedSFIAScreening
  );

  const qualitativeTitle = isSFIATestgroup
    ? t('SKILL_MATCHING_RESULTS_TITLE')
    : selectedTestgroup.name;

  // Effects
  useEffect(() => {
    const getBarElements = () => {
      const elements = {};
      Object.keys(barRefs.current).forEach((id) => {
        const element = document.getElementById(id);
        if (element) {
          elements[id] = element;
        }
      });
      setBarElements(elements);
    };
    getBarElements();
    const observer = new MutationObserver(getBarElements);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [selectedTestgroup]);

  useEffect(() => {
    if (testgroups.length > 0 && someSFIATestGroup(testgroups)) {
      let sfiaTestgroup = {
        id: PromptSubTypes.SFIA_CORRECTION,
        name: t('SKILL_MATCHING_RESULTS_TITLE'),
        completeData: {
          qualitativeConfig: [],
          qualitativeConfigResults: {},
        },
        testGroupQualitativeResults: {},
        tests: [],
      };
      let testgroupsModified;
      if (testgroups.length === 1) {
        testgroupsModified = [sfiaTestgroup];
      } else {
        const filteredTestgroups = testgroups.filter((tg) => !isSFIATestGroup(tg));
        testgroupsModified = [...filteredTestgroups, sfiaTestgroup];
      }
      setTestgroupsToQualitative(testgroupsModified);
      setSelectedTestgroup(testgroupsModified[0]);
    } else {
      setTestgroupsToQualitative(testgroups);
    }
  }, [testgroups]);

  // Functions
  const handleSelectTestgroup = (tg) => {
    setSelectedTestgroup(testgroupsToQualitative.find((item) => item.id === tg.id));
  };

  const addCandidateRole = async () => {
    if (!screeningInstance?.candidateRole) {
      setScreeningInstance({ ...screeningInstance, candidateRole });
    }
    setModalCandidateRole(false);
    await generateQualitativeReport(true, screeningInstance?.candidateRole ?? candidateRole);
  };

  const selectDownloadQualitativeReportOption = async (withAI) => {
    if (withAI) {
      if (screeningInstance?.candidateRole) {
        await generateQualitativeReport(true, screeningInstance.candidateRole);
      } else {
        document.activeElement?.blur();
        setCandidateRole('');
        setModalCandidateRole(true);
      }
    } else {
      await generateQualitativeReport(false);
    }
  };

  const calculateRangeWidth = (section) => {
    const availableWidth = 200;
    const { minPercent, maxPercent } = section;
    const differenceBetweenPercents = Math.abs(minPercent - maxPercent);

    return `${(differenceBetweenPercents * 100) / availableWidth}%`;
  };

  const calculatePointPosition = (bar) => {
    const result = selectedTestgroup.testGroupQualitativeResults.axisResults.find(
      (result) => result.axisId === bar.id
    );

    if (!result) {
      return 0;
    }

    const parsedRatio = parseFloat(result.ratio);

    if (isIPVTestgroup()) {
      return parsedRatio * 100;
    }
    return (parsedRatio + 1) * 50;
  };
  const generateQualitativeReport = async (withAI, candidateRole = null) => {
    setReportLoading(true);
    try {
      let res = null;
      if (isIPVTestgroup()) {
        res = await getCandidateQualitativeIPVPdfExport({
          recruitSessionId,
          profileId: encodeURI(profile?.id),
          partnerId: user?.id,
        });
        const filename = `${profile.firstName.toLowerCase()}-${profile.lastName.toLowerCase()}.pdf`;
        const blob = new Blob([res], { type: 'application/pdf' });
        downloadFile(blob, filename);
      } else {
        res = await getCandidateQualitativePdfExport({
          recruitSessionId,
          profileId: encodeURI(profile?.id),
          testgroupId: selectedTestgroup?.id,
          demo: false,
          withAI,
          candidateRole: candidateRole ?? null,
        });
        res?.forEach((base64String, index) => {
          const filename = `${profile.firstName.toLowerCase()}-${profile.lastName.toLowerCase()}-qualitative-${
            index + 1
          }.pdf`;
          downloadBase64Pdf(base64String, filename);
        });
      }
    } catch (error) {
      console.error('Error generating qualitative report:', error);
    } finally {
      setReportLoading(false);
    }
  };
  const getQualitativeResult = (sectionId, configId) => {
    return (
      sectionId ==
      selectedTestgroup.testGroupQualitativeResults.axisResults.find(
        (result) => result.axisId === configId
      )?.resultId
    );
  };

  const qualitativeResultsText = (config) => {
    let text = null;
    if (isIPVTestgroup()) {
      const keys = config?.extremes?.[0]?.shortName?.split(',');
      text =
        selectedTestgroup?.testGroupQualitativeResults?.result?.[`${keys?.[0]}`]?.[`${keys?.[1]}`];
    } else {
      const axisData = selectedTestgroup?.testGroupQualitativeResults?.axisResults?.find(
        (axis) => axis?.axisId === config?.id
      );
      const resultObj = config?.results?.find((result) => {
        return result?.id === axisData?.resultId;
      });
      text = resultObj?.text;
    }
    return text?.replace('<:name>', profile?.firstName) || '';
  };

  const mapScoreToPercent = (score) => {
    if (typeof score !== 'number') return 0;
    const min = 0;
    const max = 7;
    if (score < min) return 0;
    if (score > max) return 100;
    return ((score - min) / (max - min)) * 95.5;
  };

  const QualitativeDownloadButton = finishedQualitative && !isSFIATestgroup && (
    <>
      {isIPVTestgroup() ? (
        <button
          type="button"
          disabled={reportLoading}
          onClick={() => generateQualitativeReport()}
          className="px-3 py-1 text-xs border-blue-principal cursor-pointer d-flex align-items-center font-weight-700  bg-white border-button"
        >
          {!reportLoading ? (
            <>
              <span className="text-blue-principal  justify-items-center mr-2">
                {t('DOWNLOAD_QUALITATIVE_REPORT')}
              </span>
              <CloudDownload fill="#4987BE" />
            </>
          ) : (
            <HorizontalLoader className="loader-report" />
          )}
        </button>
      ) : (
        <DropdownSelect
          key="download-report-selector"
          options={downloadOptions}
          className="w-100 position-relative d-flex align-items-center"
          selected={null}
          selectedPlaceholder
          placeholderClassname="mr-2 text-xs font-weight-500"
          minWidth="12rem"
          axHeight={false}
          placeholder={
            reportLoading ? (
              <HorizontalLoader className="loader-report ml-4" />
            ) : (
              t('DOWNLOAD_QUALITATIVE_REPORT')
            )
          }
          setState={(e) => {
            if (e?.id !== undefined) selectDownloadQualitativeReportOption(e.id);
          }}
          disabled={reportLoading}
          showDownloadCloud={true}
        />
      )}
    </>
  );

  return (
    <div data-testid="qualitative-collapsed-container" className="d-flex flex-column">
      {testgroupsToQualitative.length <= 1 ? (
        <div className="d-flex justify-content-between py-2 ">
          <span
            data-testid="qualitative-collapsed-testgroup-name"
            className="my-2  text-muted text-base font-weight-600"
          >
            {qualitativeTitle}
          </span>
          {QualitativeDownloadButton}
        </div>
      ) : (
        <div className=" d-flex flex-column flex-sm-row my-2 justify-content-between align-items-end align-items-sm-center">
          <DropdownSelect
            maxHeight={false}
            className="w-100 position-relative d-flex align-items-center"
            setState={handleSelectTestgroup}
            selected={selectedTestgroup.id}
            selectedPlaceholder
            placeholderClassname="mr-2 text-xs font-weight-500"
            minWidth="12rem"
            menuClassnames="translate-options-dropdown"
            options={testgroupsToQualitative.map((tg) => ({ id: tg.id, name: tg.name }))}
            key={selectedTestgroup.id}
          />
          {QualitativeDownloadButton}
        </div>
      )}
      {finishedQualitative ? (
        isSFIATestgroup ? (
          <div className="d-flex flex-column">
            {sfiaTestgroups.map((testgroup, index) => (
              <div
                key={`${testgroup.id}-sfia-element-${index}`}
                className="d-flex flex-column gap-1 my-2"
              >
                <p>{testgroup.name}</p>
                <div
                  className={`qualitative-result-bar position-relative b-radius-8 ${
                    index % 2 ? 'qualitative-result-bar-color-1' : 'qualitative-result-bar-color-2'
                  } qualitative-result-bar-color-3`}
                  style={{ width: '100%' }}
                >
                  <span
                    style={{
                      left: `${mapScoreToPercent(testgroup?.results?.score ?? 1)}%`,
                    }}
                    data-testid="qualitative-result-bar-point"
                    className="qualitative-result-point position-absolute"
                  />
                </div>
                <span
                  data-testid="qualitative-collapsed-result-description"
                  className=" font-weight-500 text-base text-light-black"
                >
                  {testgroup?.results?.explanation}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="d-flex flex-column">
            {selectedTestgroup.completeData.qualitativeConfig.map((config) => (
              <div key={config.id} className="my-2">
                <div className="w-100 d-flex justify-content-between mb-2">
                  {isIPVTestgroup() ? (
                    <span
                      key={config.id}
                      data-testid={`qualitative-bar-${0}-extreme`}
                      className={` font-weight-600 text-base ${'qualitative-extreme-selected'}`}
                    >
                      {config.name}
                    </span>
                  ) : (
                    config.extremes.map((extreme, index) => (
                      <span
                        key={extreme.id}
                        data-testid={`qualitative-bar-${index}-extreme`}
                        className={` font-weight-600 text-base ${
                          selectedTestgroup.completeData.qualitativeConfigResults &&
                          getQualitativeResult(extreme.id, config.id)
                            ? 'qualitative-extreme-selected'
                            : 'text-light-black'
                        }`}
                      >
                        {extreme.name}
                      </span>
                    ))
                  )}
                </div>
                <div id={`${config.id}`} className="qualitative-result-bar position-relative mb-3">
                  {config.results.map((section, index) => {
                    const sectionWidth = calculateRangeWidth(section);
                    return (
                      <span
                        key={section.id}
                        data-testid="progress-bar-section"
                        style={{ width: sectionWidth }}
                        className={`qualitative-result-bar ${index === 0 && 'b-left-radius-8'} ${
                          index === config.results.length - 1 && 'b-right-radius-8'
                        } ${
                          index % 2
                            ? 'qualitative-result-bar-color-1'
                            : 'qualitative-result-bar-color-2'
                        }
                      ${
                        getQualitativeResult(section?.id, config?.id) &&
                        'qualitative-result-bar-color-3'
                      }
                      `}
                      />
                    );
                  })}
                  <span
                    style={{
                      left: `${calculatePointPosition(config)}%`, // Use percentage for positioning
                    }}
                    data-testid="qualitative-result-bar-point"
                    className="qualitative-result-point position-absolute"
                  />
                </div>
                <div>
                  <span
                    data-testid="qualitative-collapsed-result-description"
                    className=" font-weight-500 text-base text-light-black"
                  >
                    {qualitativeResultsText(config)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        <span data-testid="quantitative-collapsed-empty-state" className=" text-xs font-weight-400">
          {t('QUALITATIVE_TEST_EMPTY_STATE')}
        </span>
      )}
      <Modal
        className="screening-profile-comment-modal"
        open={modalCandidateRole}
        onCancel={() => {
          setModalCandidateRole(false);
          setCandidateRole('');
        }}
        closeIcon={<img src={closeIcon} alt={t('CLOSE_ALT')} />}
        footer={
          <button
            onClick={addCandidateRole}
            className="rounded-0 font-weight-bold d-flex align-items-center border-0 outline-none px-4 py-2  text-white bg-maya-blue"
            type="button"
            disabled={!screeningInstance?.candidateRole && candidateRole?.length < 10}
          >
            {t('DOWNLOAD_REPORT_MODAL_BUTTON')}
          </button>
        }
      >
        <h3>
          {screeningInstance?.candidateRole
            ? t('SHOW_ROLE_MODAL_TITLE')
            : t('NEW_ROLE_MODAL_TITLE')}
        </h3>
        <input
          id="candidateRole"
          className="rounded p-2 outline-none w-100 mt-4 border candidate-role-input-height text-xs"
          placeholder={t('ADD_ROLE_MODAL_PLACEHOLDER')}
          value={screeningInstance?.candidateRole ?? candidateRole}
          onChange={(e) => {
            const value = e.target.value;

            if (value.length < 50) {
              setCandidateRole(value);
              if (value.trim().length < 10) {
                setRoleError(t('CANDIDATE_ROLE_TOO_SHORT_ERROR'));
              } else {
                setRoleError('');
              }
            }
          }}
          type="text"
          maxLength={50}
          disabled={screeningInstance?.candidateRole}
        />
        {roleError && (
          <p className="danger-text text-xs m-0 mt-1 p-0" data-testid="candidate-role-input-error">
            {roleError}
          </p>
        )}
      </Modal>
    </div>
  );
};

export default QualitativeCollapsed;
